import { type Comparison, type Condition, type Operand } from "@fobu/schema";
import { isEmptyValue, toNumber, toText } from "./expression";

export interface ConditionContext {
  /** Antworten nach Feld-ID (nur sichtbare/gültige Felder). */
  answers: Record<string, unknown>;
  hidden: Record<string, string>;
  vars: Record<string, number | string>;
}

export function resolveOperand(op: Operand, ctx: ConditionContext): unknown {
  switch (op.kind) {
    case "field":
      return ctx.answers[op.fieldId] ?? null;
    case "hidden":
      return ctx.hidden[op.name] ?? null;
    case "variable":
      return ctx.vars[op.name] ?? null;
    case "value":
      return op.value;
  }
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?$/;
const TIME = /^\d{2}:\d{2}$/;

const norm = (v: unknown) => toText(v).trim().toLocaleLowerCase("de");
const asList = (v: unknown): string[] => (Array.isArray(v) ? v.map(String) : isEmptyValue(v) ? [] : [toText(v)]);

function compareOrdered(a: unknown, b: unknown): number | null {
  if (isEmptyValue(a) || isEmptyValue(b)) return null;
  // Datum/Zeit lexikografisch, sonst numerisch.
  if (typeof a === "string" && typeof b === "string" && ((ISO_DATE.test(a) && ISO_DATE.test(b)) || (TIME.test(a) && TIME.test(b)))) {
    return a < b ? -1 : a > b ? 1 : 0;
  }
  const x = toNumber(a);
  const y = toNumber(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

function equals(a: unknown, b: unknown): boolean {
  if (isEmptyValue(a) || isEmptyValue(b)) return isEmptyValue(a) && isEmptyValue(b);
  if (Array.isArray(a) && Array.isArray(b)) {
    const sa = new Set(a.map(String));
    return sa.size === new Set(b.map(String)).size && b.every((x) => sa.has(String(x)));
  }
  // Mehrfachauswahl „ist“ Option → enthält Option.
  if (Array.isArray(a)) return a.map(String).includes(toText(b));
  if (Array.isArray(b)) return b.map(String).includes(toText(a));
  if (typeof a === "number" || typeof b === "number") return toNumber(a) === toNumber(b);
  if (typeof a === "boolean" || typeof b === "boolean") {
    const bool = (v: unknown) => (typeof v === "boolean" ? v : ["true", "ja", "yes", "1"].includes(norm(v)));
    return bool(a) === bool(b);
  }
  return norm(a) === norm(b);
}

export function evaluateComparison(c: Comparison, ctx: ConditionContext): boolean {
  const left = resolveOperand(c.left, ctx);
  const right = c.right ? resolveOperand(c.right, ctx) : null;
  switch (c.operator) {
    case "is_empty":
      return isEmptyValue(left);
    case "is_not_empty":
      return !isEmptyValue(left);
    case "equals":
      return equals(left, right);
    case "not_equals":
      return !equals(left, right);
    case "contains":
      return Array.isArray(left) ? asList(left).includes(toText(right)) : norm(left).includes(norm(right));
    case "not_contains":
      return !(Array.isArray(left) ? asList(left).includes(toText(right)) : norm(left).includes(norm(right)));
    case "starts_with":
      return norm(left).startsWith(norm(right));
    case "ends_with":
      return norm(left).endsWith(norm(right));
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const cmp = compareOrdered(left, right);
      if (cmp === null) return false;
      return c.operator === "gt" ? cmp > 0 : c.operator === "gte" ? cmp >= 0 : c.operator === "lt" ? cmp < 0 : cmp <= 0;
    }
    case "includes_any": {
      const l = asList(left);
      return asList(right).some((x) => l.includes(x));
    }
    case "includes_all": {
      const l = asList(left);
      const r = asList(right);
      return r.length > 0 && r.every((x) => l.includes(x));
    }
    case "includes_none": {
      const l = asList(left);
      return !asList(right).some((x) => l.includes(x));
    }
  }
}

export function evaluateCondition(cond: Condition | undefined, ctx: ConditionContext): boolean {
  if (!cond) return true;
  if (cond.type === "comparison") return evaluateComparison(cond, ctx);
  if (cond.conditions.length === 0) return true;
  return cond.combinator === "and"
    ? cond.conditions.every((c) => evaluateCondition(c, ctx))
    : cond.conditions.some((c) => evaluateCondition(c, ctx));
}

/** Alle Operanden einer Bedingung (für Lint/Abhängigkeiten). */
export function collectOperands(cond: Condition | undefined, out: Operand[] = []): Operand[] {
  if (!cond) return out;
  if (cond.type === "comparison") {
    out.push(cond.left);
    if (cond.right) out.push(cond.right);
  } else cond.conditions.forEach((c) => collectOperands(c, out));
  return out;
}
